import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MonitorSmartphone, LogOut } from "lucide-react";
import { toast } from "sonner";
import { getSessions, revokeSession, revokeOtherSessions } from "../../lib/api";

const ago = (iso) => {
    const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    return m < 2 ? "active now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

// My Account: every device signed into this account, each with its own sign-out.
export function SignedInDevicesCard({ Card }) {
    const qc = useQueryClient();
    const { data: rows = [] } = useQuery({ queryKey: ["sessions"], queryFn: getSessions, staleTime: 30000 });
    const refresh = () => qc.invalidateQueries({ queryKey: ["sessions"] });
    const out = async (sid) => { if (!window.confirm("Sign this device out?")) return; try { await revokeSession(sid); toast.success("Device signed out"); refresh(); } catch { toast.error("Couldn't sign it out"); } };
    const outAll = async () => { if (!window.confirm("Sign out every other device?")) return; try { const r = await revokeOtherSessions(); toast.success(`${r.signed_out} device${r.signed_out === 1 ? "" : "s"} signed out`); refresh(); } catch { toast.error("Couldn't sign them out"); } };
    const others = rows.filter((r) => !r.current).length;
    return (
        <Card icon={MonitorSmartphone} title="Signed-In Devices" testid="account-devices-card">
            <div className="space-y-1.5 mb-3" data-testid="devices-list">
                {rows.length === 0 && <p className="text-[12px] text-white/45">No devices yet.</p>}
                {rows.map((r) => (
                    <div key={r.sid} className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/10 px-3 py-2" data-testid={`device-${r.sid}`}>
                        <MonitorSmartphone className="h-4 w-4 text-white/60 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-white truncate">{r.device}{r.current && <span className="ml-2 text-[10px] font-black uppercase text-[#22C55E]">This device</span>}</div>
                            <div className="text-[11px] text-white/50">Signed in {String(r.created_at).slice(0, 10)} · {ago(r.last_seen)}</div>
                        </div>
                        {!r.current && <button type="button" onClick={() => out(r.sid)} data-testid={`device-signout-${r.sid}`} className="ml-auto inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[11px] font-black uppercase text-white/60 hover:text-[#EF4444] hover:bg-white/5"><LogOut className="h-3.5 w-3.5" />Sign out</button>}
                    </div>
                ))}
            </div>
            {others > 0 && (
                <button type="button" onClick={outAll} data-testid="devices-signout-others"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-[#EF4444]/50 text-[#F87171] text-xs font-black uppercase tracking-wider hover:bg-[#EF4444]/10">
                    <LogOut className="h-4 w-4" />Sign out all other devices
                </button>
            )}
        </Card>
    );
}
